/* «Фотосессия из селфи», «Семейный портрет» и шаблоны-фотосессии (APP-SPEC-5 §4; пайплайн n8n people_photoshoot).
   Порядок блоков §4.1: шапка «… · 30 C за кадр» → «Кто в кадре» (на месте рамки) → «Как будет выглядеть» с мозаикой →
   лента «Образы» («Свой референс» первым) → карточка «Ещё настройки» (Формат · Съёмка · Пожелания) → «Создать N кадров · M C».
   Каждый блок включается по полю формы сервера; данные образов и фотосессий — GET /app/studio/screens/shoot. */
import { useEffect, useState } from 'react';
import { api, endpoints } from '../../api';
import type { UserAvatar } from '../../api/people';
import { uiFormat, uiText } from '../../i18n';
import { ChoiceTiles } from '../../ui/ChoiceTiles';
import { PromptChip } from '../../ui/PromptChip';
import { TextEditor } from '../../ui/TextEditor';
import { stagePhotoResult } from '../photo/photo-result-entry';
import type { StudioApp } from '../studio/studio-api';
import { artworkUrl } from '../studio/studio-art-model';
import { record } from '../studio/studio-content';
import { StudioSettings, type StudioSettingSlot } from '../studio/StudioFormParts';
import { StudioPhotoTray } from '../studio/StudioPhotoTray';
import { StudioSheet } from '../studio/StudioSheet';
import { useStudioDocument } from '../studio/use-studio-document';
import { useStudioForm } from '../studio/use-studio-form';
import { useStudioPhotoPreviews } from '../studio/use-studio-photo-previews';
import { CreateFoot, RightsBox, ScreenHead, TextSlot } from './kit/FormParts';
import { decode, encode, fieldOf, valueOf, limitOf, orderPrice, photoSlots, plural, referenceSlots, shootSummary, unitPrice, writeField } from './kit/form-model';
import { OrderTail } from './kit/OrderTail';
import { PeoplePicker } from './kit/PeoplePicker';
import { selfFace } from './kit/people-model';
import { GridSheet, RailCard, RailHeader, type RailItem } from './kit/Rail';
import { ReferenceCard, ReferencePlaque } from './kit/ReferenceCard';
import { useReference } from './kit/use-reference';
import { ShootingBlock } from './kit/ShootingBlock';
import { usePeopleData } from './kit/use-people-data';
import { chosenLooks, frames, shootKeys, shootMissing, shootScreen, type Look } from './shoot-model';
import './shoot.css';
import './shoot-native.css';
import { PROMPT_MAX } from '../studio/prompt-limit';

interface Person {
  person_id: number;
  action?: string;
}
const FORMAT_NAMES: Record<string, string> = { '3:4': 'Портрет', '4:5': 'Пост', '9:16': 'Сторис', '1:1': 'Квадрат' };
const FORMAT_SUBS: Record<string, string> = { '3:4': 'Для портрета', '4:5': 'Для ленты соцсетей', '9:16': 'На весь экран телефона', '1:1': 'Для аватарки и соцсетей' };
const shotsWord = (count: number) => plural(count, uiText('кадр'), uiText('кадра'), uiText('кадров'));
const looksWord = (count: number) => plural(count, uiText('образ'), uiText('образа'), uiText('образов'));

export function ShootOrderForm({ app, avatar, initialInputs }: { app: StudioApp; avatar: UserAvatar | null; initialInputs?: Record<string, unknown> }) {
  const state = useStudioForm({ app, avatar, initialInputs });
  const { form, inputs, locked } = state;
  const screenDoc = useStudioDocument(signal => api.request<Record<string, unknown>>('/app/studio/screens/shoot', { signal }), 'shoot-screen');
  const data = record(screenDoc.data);
  const screen = shootScreen(data, form, inputs, { ...record(app.entry), ...(initialInputs ?? {}) });
  const keys = shootKeys(form);
  const people = usePeopleData(!!keys.chars);
  const [sheet, setSheet] = useState<'looks' | 'shots' | { look: Look } | null>(null);
  const [hint, setHint] = useState('');
  const [editing, setEditing] = useState<number | null>(null);
  const photos = photoSlots(form);
  const previews = useStudioPhotoPreviews(state, photos);
  const referenceFields = screen.kind.id === 'neuro' ? referenceSlots(form).slice(0, 1) : [];
  const reference = useReference(state, referenceFields, setHint);
  const looks = chosenLooks(screen, inputs, keys.looks);
  const lookList = looks.map(id => screen.looks.find(look => look.id === id)).filter((look): look is Look => !!look);
  const count = frames(screen, looks, reference.on);
  const unit = unitPrice(form, data, 'shoot');
  const price = form ? orderPrice(form, unit, count) : { total: 0, confirmed: true };
  const me = selfFace(people, avatar);
  const persons = decode<Person[]>(keys.persons ? inputs[keys.persons] : undefined, []);
  const chars = decode<Array<{ avatar_id: number }>>(keys.chars ? inputs[keys.chars] : undefined, []);
  const selected = ['me', ...persons.map(person => `person:${person.person_id}`), ...chars.map(char => `char:${char.avatar_id}`)];
  const wishField = keys.wishes ? fieldOf(form, keys.wishes) : undefined;
  const wishLimit = limitOf(wishField, PROMPT_MAX);
  const set = (key: string | undefined, value: unknown) => key && writeField(state.setInputs, form, key, value);

  /* «Вы» в заказ — тем, что сейчас стоит за «Вы» в аккаунте (§4.4): свои фото или выбранный вариант */
  const meValue = people.self?.src === 'avatar' && people.self.pick ? { src: 'avatar', avatar_id: Number(people.self.pick) } : { src: 'photos' };
  const meInput = keys.me && me.ready ? JSON.stringify(meValue) : '';
  const meKey = keys.me;
  const setInputs = state.setInputs;
  useEffect(() => {
    if (!meKey || locked) return;
    setInputs(current => {
      if ((current[meKey] ?? '') === meInput) return current;
      const next = { ...current };
      if (meInput) next[meKey] = meInput;
      else delete next[meKey];
      return next;
    });
  }, [meKey, meInput, locked, setInputs]);
  /* Какая фотосессия и (если образы не выбираются) её образы — производные экрана, а не выбор человека */
  const fixedInputs = (() => {
    const result: Record<string, string> = {};
    const put = (key: string | undefined, value: unknown) => {
      const encoded = key ? encode(fieldOf(form, key), value) : undefined;
      if (key && encoded !== undefined) result[key] = encoded;
    };
    put(keys.shootId, screen.kind.id);
    if (!screen.kind.pick && screen.kind.looks.length) put(keys.looks, screen.kind.looks);
    return JSON.stringify(result);
  })();
  useEffect(() => {
    if (locked) return;
    const fixed = JSON.parse(fixedInputs) as Record<string, string>;
    setInputs(current => (Object.entries(fixed).every(([key, value]) => current[key] === value) ? current : { ...current, ...fixed }));
  }, [fixedInputs, locked, setInputs]);

  const toggleLook = (id: string) => {
    if (!screen.kind.pick || !keys.looks) return;
    const next = looks.includes(id) ? looks.filter(value => value !== id) : [...looks, id];
    set(keys.looks, reference.on ? [...next, 'ref'] : next);
  };
  const setReference = (on: boolean) => keys.looks && set(keys.looks, on ? [...looks, 'ref'] : looks);
  const togglePerson = (value: string) => {
    if (value.startsWith('person:')) {
      const id = Number(value.slice(7));
      set(keys.persons, persons.some(person => person.person_id === id) ? persons.filter(person => person.person_id !== id) : [...persons, { person_id: id }]);
    } else if (value.startsWith('char:')) {
      const id = Number(value.slice(5));
      set(keys.chars, chars.some(char => char.avatar_id === id) ? chars.filter(char => char.avatar_id !== id) : [...chars, { avatar_id: id }]);
    }
  };
  const setAction = (id: number, action: string) =>
    set(
      keys.persons,
      persons.map(person => (person.person_id === id ? { ...person, ...(action ? { action } : { action: undefined }) } : person)),
    );

  const items: RailItem[] = screen.looks.map(look => ({
    id: look.id,
    name: look.name,
    image: look.cover,
    badge: look.hit ? 'Хит' : look.isNew ? 'Новое' : undefined,
    count: shotsWord(look.shots.length || screen.shotsPerLook),
    themes: look.themes,
    popularity: look.popularity,
    isNew: look.isNew,
    search: look.description,
  }));
  /* как railLooks шаблона (assets/bot-shoot.js): восемь популярных на своих местах, впереди — только выбранные сверх них; нажатый популярный образ не прыгает */
  const popular = [...items].sort((a, b) => (b.popularity ?? 0) - (a.popularity ?? 0)).slice(0, 8);
  const railItems = screen.kind.pick
    ? [...looks.flatMap(id => items.filter(item => item.id === id && !popular.includes(item))), ...popular]
    : items.filter(item => screen.kind.looks.includes(item.id) || !screen.kind.looks.length);
  const lookLabel = lookList.length === 1 ? `${lookList[0].name} · ${shotsWord(count)}` : lookList.length ? `${looksWord(lookList.length + (reference.on ? 1 : 0))} · ${shotsWord(count)}` : reference.on ? shotsWord(count) : '';
  const mosaic = lookList.flatMap(look => look.shots.filter(shot => shot.image).map(shot => ({ look, shot }))).slice(0, 3);

  const slots: StudioSettingSlot[] = [
    ...(keys.ratio ? [{ title: 'Формат', value: FORMAT_NAMES[valueOf(form, inputs, keys.ratio)] ?? (screen.formats.find(item => item.id === valueOf(form, inputs, keys.ratio))?.name || 'Портрет') }] : []),
    ...(keys.shoot && screen.kind.id !== 'app' ? [{ title: 'Съёмка', value: shootSummary(inputs.shoot) }] : []),
    ...(wishField && !wishField.required ? [{ title: 'Пожелания', value: valueOf(form, inputs, keys.wishes) || 'Добавить' }] : []),
  ];
  const missing = form
    ? shootMissing({
        form,
        inputs,
        screen,
        looks,
        reference: reference.on,
        meReady: me.ready,
        rights: state.rights,
        photoFields: photos.map(slot => slot.key),
        wishLimit,
        priceConfirmed: price.confirmed,
      })
    : null;
  /* Образы — только когда форма принимает выбор образа или вид фотосессии: иначе лента была бы неработающей витриной */
  const showLooks = !!keys.looks || !!keys.shootId;
  const ratioField = keys.ratio ? fieldOf(form, keys.ratio) : undefined;
  const ratioValues = ratioField?.values.length ? ratioField.values : screen.formats.map(item => item.id);

  return (
    <section className="native-studio-form shk-shoot pf-in">
      {/* На странице заказа заголовок экрана не повторяется: название уже в шапке (кадры *-done) */}
      {!state.orderId && <ScreenHead title={app.name} unit={unit !== null ? uiFormat('{0} C за кадр', unit) : null} />}
      {!form && !state.error && <p role="status">{uiText('Загружаем…')}</p>}
      {(state.error || screenDoc.error) && (
        <p role="alert">
          {uiText(state.error || 'Не удалось загрузить образы фотосессии.')}{' '}
          <button
            type="button"
            disabled={state.busy}
            onClick={() => {
              if (screenDoc.error) screenDoc.reload();
              if (state.error) state.setRevision(value => value + 1);
            }}
          >
            {uiText('Повторить')}
          </button>
        </p>
      )}
      {form && !state.orderId && (
        <form
          onSubmit={event => {
            event.preventDefault();
            if (!state.busy && !state.submissionBlocked && (state.intent || (!missing && form.available))) void state.submit();
          }}
        >
          {(keys.me || keys.persons || keys.chars) && (
            <PeoplePicker
              data={people}
              avatar={avatar}
              mode="multi"
              selected={selected}
              limit={screen.kind.maxPeople}
              locked={locked}
              onToggle={togglePerson}
              allowPets={screen.kind.id !== 'family'}
              withCharacters={!!keys.chars}
              caption="Только свои фото или с согласием человека на фото"
            >
              {!!persons.length && (
                <div className="ps-actf shk-actions">
                  <div className="shk-people-sub">
                    <span>{uiText('Что делают в кадре')}</span>
                    <small>{uiText('необязательно')}</small>
                  </div>
                  {persons.map(person => {
                    const who = people.persons.find(item => item.id === person.person_id);
                    const custom = !!person.action && !screen.kind.actions.includes(person.action);
                    return (
                      <div key={person.person_id} className="photo-person-action">
                        <b>{who?.name ?? uiText('Человек')}</b>
                        <div className="chips" role="group" aria-label={`${uiText('Что делает в кадре')} · ${who?.name ?? ''}`}>
                          {screen.kind.actions.map(action => (
                            <PromptChip
                              key={action}
                              label={uiText(action)}
                              variant="action"
                              selected={person.action === action}
                              disabled={locked}
                              onClick={() => setAction(person.person_id, person.action === action ? '' : action)}
                            />
                          ))}
                          <button type="button" className={`chip${custom ? ' on' : ''}`} disabled={locked} onClick={() => setEditing(person.person_id)}>
                            {custom ? person.action : uiText('Свой вариант')}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </PeoplePicker>
          )}

          {!!photos.length && (
            <StudioPhotoTray
              state={state}
              slots={photos}
              urls={previews.urls}
              minimum={photos.filter(slot => slot.required).length || 1}
              title="Ваши фото"
              description="Лицо крупно, при хорошем свете"
              consent={<RightsBox checked={state.rights} disabled={locked} text="Подтверждаю согласие людей на фото" onChange={state.setRights} />}
            />
          )}
          {!photos.length && !!referenceFields.length && (
            <RightsBox checked={state.rights} disabled={locked} text="Подтверждаю право использовать загружаемые картинки" onChange={state.setRights} />
          )}

          {showLooks && (!!screen.looks.length || reference.on) && (
            <div className="shk-mosaic">
              <div className="glbl ps-gl">
                {uiText('Как будет выглядеть')}
                {lookLabel ? (
                  <button type="button" className="lnk ps-glk" onClick={() => setSheet('shots')} aria-label={uiFormat('Смотреть кадры: {0}', lookLabel)}>
                    <span>{lookLabel}</span> ›
                  </button>
                ) : null}
              </div>
              <div className="ps-mo">
                {[0, 1, 2].map(index => {
                  const cell = mosaic[index];
                  const extra = lookList.length > 3 && index === 2 ? lookList.length - 2 : 0;
                  return cell ? (
                    <button type="button" key={index} className="c" onClick={() => setSheet({ look: cell.look })} aria-label={`${cell.look.name}: ${cell.shot.name}`}>
                      {artworkUrl(cell.shot.image) && <img className="pf-b" src={artworkUrl(cell.shot.image)!} alt="" />}
                      {!me.ready && <span className="tg">{uiText('Пример')}</span>}
                      {extra > 0 && <span className="more">+{extra}</span>}
                    </button>
                  ) : screen.kind.pick && index === mosaic.length ? (
                    <button type="button" key={index} className="c add" disabled={locked} onClick={() => setSheet('looks')}>
                      <span className="ic">+</span>
                      <small>{uiText(index ? 'Ещё образ' : 'Выбрать образ')}</small>
                    </button>
                  ) : (
                    <span key={index} className="c empty" />
                  );
                })}
              </div>
            </div>
          )}

          {showLooks && (!!screen.looks.length || !!referenceFields.length) && (
            <div className="bcs ss-rail shk-rail">
              <RailHeader
                title={screen.kind.id === 'app' ? 'Кадры' : 'Образы'}
                total={screen.looks.length}
                locked={locked}
                onAll={screen.kind.pick ? () => setSheet('looks') : undefined}
              />
              <div className="bcs-r nosb" role="group" aria-label={uiText('Образы')}>
                {!!referenceFields.length && (
                  <ReferenceCard state={state} model={reference} onSelected={() => setReference(true)} onDeselected={() => setReference(false)} />
                )}
                {railItems.map(item => (
                  <RailCard
                    key={item.id}
                    item={item}
                    on={screen.kind.pick ? looks.includes(item.id) : false}
                    locked={locked}
                    onPick={() => (screen.kind.pick ? toggleLook(item.id) : setSheet({ look: screen.looks.find(look => look.id === item.id)! }))}
                    onPeek={() => setSheet({ look: screen.looks.find(look => look.id === item.id)! })}
                  />
                ))}
                {screen.kind.pick && screen.looks.length > railItems.length && (
                  <button type="button" className="bcs-c shk-card more" disabled={locked} onClick={() => setSheet('looks')}>
                    <span className="pc">
                      <b className="mono">{screen.looks.length}</b>
                    </span>
                    <b>{uiText('Все образы')}</b>
                  </button>
                )}
              </div>
              <ReferencePlaque state={state} model={reference} shield="Берём образ: одежду, позу, фон и свет. В кадре будете вы, лица с картинки не переносим" />
            </div>
          )}
          {screenDoc.loading && !screenDoc.data && <p role="status">{uiText('Загружаем образы…')}</p>}
          {hint && <p role="status">{hint}</p>}

          {wishField?.required && (
            <TextSlot
              label="Пожелания"
              value={valueOf(form, inputs, keys.wishes)}
              limit={limitOf(wishField, PROMPT_MAX)}
              disabled={locked}
              chips={screen.wishes}
              optional={false}
              note="Модерация после списания"
              onChange={value => set(keys.wishes, value)}
            />
          )}

          {!!slots.length && (
            <StudioSettings slots={slots} disabled={locked}>
              {keys.ratio && !!ratioValues.length && (
                <ChoiceTiles
                  label={uiText('Формат')}
                  variant="rows"
                  value={valueOf(form, inputs, keys.ratio) || ratioValues[0]}
                  disabled={locked}
                  onChange={value => set(keys.ratio, value)}
                  choices={ratioValues.map(value => ({
                    value,
                    label: uiText(FORMAT_NAMES[value] ?? screen.formats.find(item => item.id === value)?.name ?? value),
                    description: uiText(FORMAT_SUBS[value] ?? screen.formats.find(item => item.id === value)?.sub ?? ''),
                  }))}
                />
              )}
              {keys.shoot && screen.kind.id !== 'app' && <ShootingBlock state={state} styled={!!lookList.length} referenced={reference.on && !lookList.length} />}
              {wishField && !wishField.required && (
                <TextSlot
                  label="Пожелания"
                  value={valueOf(form, inputs, keys.wishes)}
                  limit={wishLimit}
                  disabled={locked}
                  chips={screen.wishes}
                  note="Модерация после списания"
                  onChange={value => set(keys.wishes, value)}
                />
              )}
            </StudioSettings>
          )}

          <CreateFoot
            label={!keys.looks ? uiFormat('Создать фотосессию · {0} C', price.total) : !count && screen.kind.pick ? 'Создать фотосессию' : uiFormat('Создать {0} · {1} C', shotsWord(count), price.total)}
            busy={state.busy}
            blocked={state.submissionBlocked}
            pending={!!state.intent}
            missing={missing}
            available={form.available}
            onCreate={() => void state.submit()}
          />
        </form>
      )}

      {sheet === 'looks' && (
        <GridSheet
          title="Все образы"
          items={items}
          isOn={id => looks.includes(id)}
          onPick={item => toggleLook(item.id)}
          onPeek={item => setSheet({ look: screen.looks.find(look => look.id === item.id)! })}
          locked={locked}
          themes={screen.themes.length ? screen.themes : [...new Set(screen.looks.flatMap(look => look.themes))]}
          tabs={[
            { id: 'all', label: uiText('Все'), filter: () => true },
            { id: 'pop', label: uiText('Популярные'), filter: item => (item.popularity ?? 0) > 0 },
            { id: 'sel', label: uiFormat('Выбранные · {0}', looks.length), filter: item => looks.includes(item.id) },
          ]}
          done={uiFormat('Готово · {0} · {1}', looksWord(looks.length), shotsWord(count))}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet === 'shots' && (
        <StudioSheet title="Кадры фотосессии" onClose={() => setSheet(null)}>
          <h2>{uiText('Кадры фотосессии')}</h2>
          <p>
            {looksWord(lookList.length + (reference.on ? 1 : 0))} · {shotsWord(count)}
            {!screen.kind.pick ? ` · ${uiText('всё входит')}` : ''}
          </p>
          {lookList.map(look => (
            <div key={look.id} className="shk-look">
              <div className="ps-lkh">
                <b>{look.name}</b>
                <span>{shotsWord(look.shots.length)}</span>
              </div>
              <div className="ps-shots shk-shots">
                {look.shots.map((shot, index) => (
                  <span key={index} className="ps-shot">
                    {artworkUrl(shot.image) ? <img src={artworkUrl(shot.image)!} alt="" /> : null}
                    <b>{shot.name}</b>
                  </span>
                ))}
              </div>
            </div>
          ))}
          {reference.on && <p>{uiFormat('Свой референс · {0}', shotsWord(screen.shotsPerLook))}</p>}
        </StudioSheet>
      )}
      {sheet && typeof sheet === 'object' && (
        <StudioSheet title={sheet.look.name} onClose={() => setSheet(null)}>
          <h2>{sheet.look.name}</h2>
          {sheet.look.description && <p>{sheet.look.description}</p>}
          <div className="ps-shots shk-shots">
            {sheet.look.shots.map((shot, index) => (
              <span key={index} className="ps-shot">
                {artworkUrl(shot.image) ? <img src={artworkUrl(shot.image)!} alt={shot.name} /> : null}
                <b>{shot.name}</b>
              </span>
            ))}
          </div>
          {screen.kind.pick && (
            <button
              type="button"
              className="btn"
              disabled={locked}
              onClick={() => {
                toggleLook(sheet.look.id);
                setSheet(null);
              }}
            >
              {uiText(looks.includes(sheet.look.id) ? 'Убрать из фотосессии' : 'Добавить в фотосессию')}
            </button>
          )}
        </StudioSheet>
      )}
      {editing !== null && (
        <TextEditor
          label={uiText('Что делает в кадре')}
          value={persons.find(person => person.person_id === editing)?.action ?? ''}
          limit={60}
          disabled={locked}
          onChange={value => setAction(editing, value)}
          onClose={() => setEditing(null)}
        />
      )}

      <OrderTail
        state={state}
        kind="shoot"
        groupLabel={app.name}
        copy={{
          running: ['Создаём фотосессию', 'Можно уйти с экрана: бот пришлёт фото в чат по мере готовности.'],
          done: ['Фотосессия готова', 'Бот уже прислал кадры в чат, в отдельную тему.'],
          failed: ['Фотосессия не создана', 'Проверьте настройки и попробуйте ещё раз.'],
        }}
        saveLabel="Скачать все"
        failLabel="Изменить настройки"
        limit={48}
        next={[
          { label: 'Сделать рилс', icon: 'film', onClick: shots => void handoff(state, shots[0]) },
          { label: 'Полароид-рилс', icon: 'list', onClick: shots => void handoff(state, shots[0]) },
        ]}
      />
    </section>
  );
}

/** «Что дальше» отдаёт кадр каталогу «Рилс с вами» тем же механизмом, что итог «Фото» (photo-result-entry). */
async function handoff(state: ReturnType<typeof useStudioForm>, shot: { index: number; blob: Blob; name: string; mime: string } | undefined) {
  if (!shot || !state.order) return;
  const me = await endpoints.me();
  if (me.user.id !== state.intent?.owner) return;
  stagePhotoResult({
    owner: me.user.id,
    orderId: state.order.id,
    revision: state.order.result_revision,
    index: shot.index,
    file: new File([shot.blob], shot.name, { type: shot.mime }),
    destination: 'reels',
  });
}
